import type { MissionItem } from '@mobile-mapping/providers';
import type { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';

import type { FxReferencesFilterMode } from './filter-fx-reference-nodes';
import type { ProjectDataLoadItemType } from './project-data-load-control';
import {
  isFxMapLayerReference,
  isSaveableFxReference,
  type FxWorkspaceTreeItem,
} from './fx-workspace-tree.types';

const addWhen = <T>(condition: boolean, values: Set<T>, value: T): void => {
  if (condition) {
    values.add(value);
  }
};

const projectDataLoadItemKey = (
  loadItemType: ProjectDataLoadItemType,
  refTrn: string
): string => `${loadItemType}:${refTrn}`;

export interface FxReferenceFilterVisibility {
  readonly selectedMissionIds: ReadonlySet<string>;
  readonly selectedReferenceTrns: ReadonlySet<string>;
  readonly visibleLayerIds: ReadonlySet<string>;
  readonly visibleLoadItemKeys: ReadonlySet<string>;
}

export interface FxReferenceVisibilityReaders {
  referenceSelected(refTrn: string): boolean;
  isLayerVisible(layerId: string): boolean;
  isLoadItemVisible(type: ProjectDataLoadItemType, refTrn: string): boolean;
}

export const collectSelectedMissionIds = (
  missions: readonly MissionItem[],
  referenceSelected: (refTrn: string) => boolean
): Set<string> => {
  const selectedMissionIds = new Set<string>();

  for (const mission of missions) {
    const missionSelected =
      referenceSelected(mission.missionTrn) ||
      referenceSelected(mission.id) ||
      (mission.pathId != null && referenceSelected(mission.pathId));
    if (missionSelected) {
      selectedMissionIds.add(mission.id);
    }
  }

  return selectedMissionIds;
};

const recordVisibleReference = (
  data: FxWorkspaceTreeItem & { kind: 'reference'; refTrn: string },
  filterMode: FxReferencesFilterMode,
  readers: FxReferenceVisibilityReaders,
  visibility: {
    selectedReferenceTrns: Set<string>;
    visibleLayerIds: Set<string>;
    visibleLoadItemKeys: Set<string>;
  }
): void => {
  if (filterMode === 'workspace') {
    addWhen(
      readers.referenceSelected(data.refTrn),
      visibility.selectedReferenceTrns,
      data.refTrn
    );
    return;
  }

  if (isFxMapLayerReference(data)) {
    addWhen(
      readers.isLayerVisible(data.layerId),
      visibility.visibleLayerIds,
      data.layerId
    );
    return;
  }

  if (
    data.loadItemType !== undefined &&
    readers.isLoadItemVisible(data.loadItemType, data.refTrn)
  ) {
    visibility.visibleLoadItemKeys.add(
      projectDataLoadItemKey(data.loadItemType, data.refTrn)
    );
  }
};

export const collectVisibleReferenceKeys = (
  nodes: readonly TreeNodeDefinition<FxWorkspaceTreeItem>[],
  filterMode: FxReferencesFilterMode,
  readers: FxReferenceVisibilityReaders
): Pick<
  FxReferenceFilterVisibility,
  'selectedReferenceTrns' | 'visibleLayerIds' | 'visibleLoadItemKeys'
> => {
  const selectedReferenceTrns = new Set<string>();
  const visibleLayerIds = new Set<string>();
  const visibleLoadItemKeys = new Set<string>();
  const visibility = {
    selectedReferenceTrns,
    visibleLayerIds,
    visibleLoadItemKeys,
  };

  for (const node of nodes) {
    const data = node.data;
    if (!isSaveableFxReference(data)) {
      continue;
    }

    recordVisibleReference(data, filterMode, readers, visibility);
  }

  return { selectedReferenceTrns, visibleLayerIds, visibleLoadItemKeys };
};

const isSelectedMissionGroupNode = (
  data: FxWorkspaceTreeItem | undefined,
  selectedMissionIds: ReadonlySet<string>
): boolean => {
  if (
    data?.missionId === undefined ||
    !selectedMissionIds.has(data.missionId)
  ) {
    return false;
  }

  // The mission path is part of the mission in this tree, the same as a run.
  // Project Data still saves it only when its own checkbox is selected.
  return (
    data.kind === 'referenceMission' ||
    data.kind === 'referenceRuns' ||
    (data.kind === 'reference' &&
      (data.loadItemType === 'pointCloud' ||
        data.loadItemType === 'trajectory'))
  );
};

// The saved reference is the map workspace id. List that workspace's layers with it.
const isLayerOfSelectedMapWorkspace = (
  data: FxWorkspaceTreeItem & { kind: 'reference'; refTrn: string },
  visibility: FxReferenceFilterVisibility
): boolean =>
  isFxMapLayerReference(data) &&
  data.mapWorkspaceId !== undefined &&
  visibility.selectedReferenceTrns.has(data.mapWorkspaceId);

const matchesActiveReference = (
  data: FxWorkspaceTreeItem & { kind: 'reference'; refTrn: string },
  visibility: FxReferenceFilterVisibility
): boolean => {
  if (isFxMapLayerReference(data)) {
    return visibility.visibleLayerIds.has(data.layerId);
  }

  return (
    data.loadItemType !== undefined &&
    visibility.visibleLoadItemKeys.has(
      projectDataLoadItemKey(data.loadItemType, data.refTrn)
    )
  );
};

export const matchesFxReferenceFilter = (
  node: TreeNodeDefinition<FxWorkspaceTreeItem>,
  filterMode: FxReferencesFilterMode,
  visibility: FxReferenceFilterVisibility
): boolean => {
  if (
    filterMode === 'workspace' &&
    isSelectedMissionGroupNode(node.data, visibility.selectedMissionIds)
  ) {
    return true;
  }

  const data = node.data;
  if (!isSaveableFxReference(data)) {
    return false;
  }

  switch (filterMode) {
    case 'active':
      return matchesActiveReference(data, visibility);
    case 'workspace':
      return (
        visibility.selectedReferenceTrns.has(data.refTrn) ||
        isLayerOfSelectedMapWorkspace(data, visibility)
      );
    default:
      throw new UnreachableCaseError(filterMode);
  }
};
